import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ExternalLink, Plus, RefreshCw } from 'lucide-react';
import {
  TaskyAlert,
  TaskyBadge,
  TaskyButton,
  TaskyEmptyState,
  TaskyErrorState,
  TaskyInput,
  TaskyLabel,
  TaskySelect,
  TaskySpinner,
  TaskyTextarea,
} from '../../../components/tasky';
import monitoringService, {
  CompetitorSuggestion,
} from '../../../services/api/knowledge/monitoringService';
import {
  ChangeDetection,
  ChangeEvent,
  CreateCompetitorPayload,
  CreateMonitoredSitePayload,
  MonitoringCompetitor,
  MonitoringSite,
  MonitoringSnapshot,
  PaginationLinks,
  PaginationMeta,
  UpdateMonitoredSitePayload,
} from '../../../types/knowledgeMonitoring';
import SimpleModal from '../../../components/common/SimpleModal';
import ChangeDetectionModal from '../../../components/knowledge/monitoring/ChangeDetectionModal';
import ChangeHistoryTab from './ChangeHistoryTab';

type CreateCompetitorFormState = {
  name: string;
  websiteUrl: string;
  description: string;
  tagsInput: string;
};

type SelectableCompetitorSuggestion = CompetitorSuggestion & {
  selected: boolean;
};

const CREATE_COMPETITOR_FORM_INITIAL: CreateCompetitorFormState = {
  name: '',
  websiteUrl: '',
  description: '',
  tagsInput: '',
};

const PAGE_TYPE_OPTIONS = [
  { value: 1, label: 'ランディングページ' },
  { value: 2, label: '料金ページ' },
  { value: 3, label: '機能ページ' },
  { value: 4, label: 'キャンペーンLP' },
  { value: 5, label: 'サポートページ' },
  { value: 6, label: '規約ページ' },
  { value: 7, label: '採用ページ' },
  { value: 0, label: 'その他' },
] as const;

const STATUS_OPTIONS = [
  { value: 1, label: '稼働中' },
  { value: 0, label: '下書き' },
  { value: 2, label: '停止中' },
  { value: 3, label: 'エラー' },
] as const;

// 監視頻度は最短1日（分・時間単位はクレジット大量消費のため廃止。
// 旧設定のサイトはサーバー側で1日間隔に丸められる）
const FREQUENCY_OPTIONS = [
  { value: 0, label: '1日ごと' },
  { value: 1, label: '3日ごと' },
  { value: 2, label: '7日ごと' },
  { value: 3, label: 'カスタム' },
] as const;

const STATUS_DISPLAY_LABEL: Record<string, string> = {
  active: '稼働中',
  draft: '下書き',
  paused: '停止中',
  error: 'エラー',
};

const RUN_STATUS_DISPLAY_LABEL: Record<string, string> = {
  scheduled: '予定',
  running: '実行中',
  succeeded: '成功',
  diff_detected: '差分あり',
  failed: '失敗',
  skipped: 'スキップ',
};

const CHANGE_TYPE_DISPLAY_LABEL: Record<string, string> = {
  content_added: 'コンテンツ追加',
  content_removed: 'コンテンツ削除',
  content_modified: '内容更新',
  price_changed: '価格変更',
  cta_changed: 'CTA変更',
  layout_changed: 'レイアウト変更',
  metadata_changed: 'メタ情報変更',
  other: 'その他',
};

const SEVERITY_DISPLAY_LABEL: Record<string, string> = {
  low: '低',
  medium: '中',
  high: '高',
  critical: '緊急',
};

const SEVERITY_TONE_CLASS: Record<string, string> = {
  low: 'severity--low',
  medium: 'severity--medium',
  high: 'severity--high',
  critical: 'severity--critical',
};

type TaskyBadgeVariant = React.ComponentProps<typeof TaskyBadge>['variant'];

const SITE_STATUS_BADGE_VARIANT: Record<string, TaskyBadgeVariant> = {
  active: 'success',
  draft: 'secondary',
  paused: 'warning',
  error: 'destructive',
};

const RUN_STATUS_BADGE_VARIANT: Record<string, TaskyBadgeVariant> = {
  scheduled: 'info',
  running: 'info',
  succeeded: 'success',
  diff_detected: 'warning',
  failed: 'destructive',
  skipped: 'secondary',
};

/** モック .cm-status の配色（見た目のみ） */
const SITE_STATUS_PILL_STYLE: Record<string, React.CSSProperties> = {
  active: { color: '#2E9B7E', background: 'rgba(68,194,164,.14)' },
  draft: { color: '#8A90A4', background: '#F2F1ED' },
  paused: { color: '#8A90A4', background: '#F2F1ED' },
  error: { color: '#C96162', background: 'rgba(201,97,98,.12)' },
};

const SITE_STATUS_PILL_FALLBACK: React.CSSProperties = { color: '#8A90A4', background: '#F2F1ED' };

/** モック .cm-run の文字色（見た目のみ） */
const RUN_STATUS_TEXT_COLOR: Record<string, string> = {
  scheduled: '#4F5BB5',
  running: '#4F5BB5',
  succeeded: '#2E9B7E',
  diff_detected: '#A87C2B',
  failed: '#C96162',
  skipped: '#8A90A4',
};

const TIMEZONE_FALLBACK = 'Asia/Tokyo';

type SiteFormState = {
  title: string;
  url: string;
  pageType: number;
  status: number;
  frequencyType: number;
  customIntervalDays: string;
  timezone: string;
};

type SiteChangeEventState = {
  events: ChangeDetection[];
  meta: PaginationMeta | null;
  links: PaginationLinks | null;
  isLoading: boolean;
  isLoadingMore: boolean;
  error: string | null;
  initialized: boolean;
};

type SiteSnapshotState = {
  snapshots: MonitoringSnapshot[];
  meta: PaginationMeta | null;
  isLoading: boolean;
  isLoadingMore: boolean;
  error: string | null;
};

const createInitialSiteForm = (): SiteFormState => ({
  title: '',
  url: '',
  pageType: 1,
  status: 1,
  frequencyType: 1,
  customIntervalDays: '',
  timezone: getPreferredTimezone(),
});

const createInitialChangeEventState = (): SiteChangeEventState => ({
  events: [],
  meta: null,
  links: null,
  isLoading: false,
  isLoadingMore: false,
  error: null,
  initialized: false,
});

const createInitialSnapshotState = (): SiteSnapshotState => ({
  snapshots: [],
  meta: null,
  isLoading: false,
  isLoadingMore: false,
  error: null,
});

function getPreferredTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone ?? TIMEZONE_FALLBACK;
  } catch (error) {
    console.warn('Failed to resolve timezone', error);
    return TIMEZONE_FALLBACK;
  }
}

/**
 * 競合のHPリンクを取得する。手動追加はwebsite_url、自動取得はcompany_urlのみ
 * 埋まっているため、website_url優先でフォールバックする（保存先はwebsite_urlのまま変更しない）。
 */
function getCompetitorWebsiteUrl(competitor: Pick<MonitoringCompetitor, 'website_url' | 'company_url'>): string {
  return competitor.website_url ?? competitor.company_url ?? '';
}

interface CompetitorTabProps {
  productId?: number;
  productName?: string;
}

const CompetitorTab: React.FC<CompetitorTabProps> = ({ productId, productName }) => {
  const [competitorSubTab, setCompetitorSubTab] = useState<'監視設定' | '変更履歴'>('監視設定');
  // ⑦: 競合監視のデフォルトはOFF。読み込み後、有効な監視サイトを持つ商品（既存の監視設定）のみONにする。
  const [monitoringEnabled, setMonitoringEnabled] = useState(false);

  interface CompetitorWithSites extends MonitoringCompetitor {
    sites: MonitoringSite[];
  }

  const [competitors, setCompetitors] = useState<CompetitorWithSites[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSuggestionModalOpen, setIsSuggestionModalOpen] = useState(false);
  const [suggestions, setSuggestions] = useState<SelectableCompetitorSuggestion[]>([]);
  const [suggestionsGeneratedAt, setSuggestionsGeneratedAt] = useState<string | null>(null);
  const [isSuggestionsLoading, setIsSuggestionsLoading] = useState(false);
  const [isRefreshingSuggestions, setIsRefreshingSuggestions] = useState(false);
  const [suggestionError, setSuggestionError] = useState<string | null>(null);
  const [isSavingSuggestions, setIsSavingSuggestions] = useState(false);
  const [suggestionSaveError, setSuggestionSaveError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const autoPromptedProductIdRef = useRef<number | null>(null);

  const triggerReload = useCallback(() => {
    setReloadKey((prev) => prev + 1);
  }, []);

  const loadSuggestions = useCallback(async (forceResearch = false) => {
    if (!productId) return;

    const keepCurrentList = forceResearch && suggestions.length > 0;
    setSuggestionError(null);
    setSuggestionSaveError(null);
    setIsSuggestionsLoading(!keepCurrentList);
    setIsRefreshingSuggestions(keepCurrentList);

    try {
      let response = forceResearch
        ? await monitoringService.researchCompetitorSuggestions(productId)
        : await monitoringService.getCompetitorSuggestions(productId);

      if (!forceResearch && response.data.length === 0) {
        response = await monitoringService.researchCompetitorSuggestions(productId);
      }

      setSuggestions(
        response.data.map((suggestion) => ({
          ...suggestion,
          selected: true,
        }))
      );
      setSuggestionsGeneratedAt(response.generated_at ?? null);
    } catch (err: any) {
      setSuggestionError(
        err?.response?.data?.message || err?.message || '競合分析の調査に失敗しました。'
      );
    } finally {
      setIsSuggestionsLoading(false);
      setIsRefreshingSuggestions(false);
    }
  }, [productId, suggestions.length]);

  const handleSuggestionModalOpen = useCallback(() => {
    if (!productId) return;
    setIsSuggestionModalOpen(true);
    void loadSuggestions(false);
  }, [loadSuggestions, productId]);

  const handleSuggestionToggle = (index: number) => {
    setSuggestions((current) =>
      current.map((suggestion, suggestionIndex) =>
        suggestionIndex === index
          ? { ...suggestion, selected: !suggestion.selected }
          : suggestion
      )
    );
  };

  const handleSaveSuggestions = async () => {
    if (!productId) return;

    const selected = suggestions.filter((suggestion) => suggestion.selected);
    if (selected.length === 0) return;

    setIsSavingSuggestions(true);
    setSuggestionSaveError(null);

    const results = await Promise.allSettled(
      selected.map((suggestion) => {
        const description = [
          suggestion.description,
          suggestion.strengths ? `強み: ${suggestion.strengths}` : null,
          suggestion.weaknesses ? `弱み: ${suggestion.weaknesses}` : null,
        ].filter(Boolean).join('\n');

        return monitoringService.createCompetitor(productId, {
          name: suggestion.name,
          website_url: suggestion.company_url || undefined,
          description: description || undefined,
          tags: ['AI調査候補'],
        });
      })
    );

    const savedCount = results.filter((result) => result.status === 'fulfilled').length;
    setIsSavingSuggestions(false);

    if (savedCount > 0) {
      setIsSuggestionModalOpen(false);
      triggerReload();
      return;
    }

    const rejected = results.find(
      (result): result is PromiseRejectedResult => result.status === 'rejected'
    );
    setSuggestionSaveError(
      rejected?.reason?.response?.data?.message || '選択した競合情報の保存に失敗しました。'
    );
  };
  const [siteChangeEvents, setSiteChangeEvents] = useState<Record<number, SiteChangeEventState>>({});

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formState, setFormState] = useState<CreateCompetitorFormState>(() => ({
    ...CREATE_COMPETITOR_FORM_INITIAL,
  }));
  const [formError, setFormError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingCompetitor, setEditingCompetitor] = useState<CompetitorWithSites | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<CompetitorWithSites | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const [isSiteModalOpen, setIsSiteModalOpen] = useState(false);
  const [siteModalCompetitor, setSiteModalCompetitor] = useState<CompetitorWithSites | null>(null);
  const [siteForm, setSiteForm] = useState<SiteFormState>(createInitialSiteForm);
  const [siteFormError, setSiteFormError] = useState<string | null>(null);
  const [isSiteSubmitting, setIsSiteSubmitting] = useState(false);
  const [editingSite, setEditingSite] = useState<MonitoringSite | null>(null);
  const [siteDeleteTarget, setSiteDeleteTarget] = useState<{
    competitor: CompetitorWithSites;
    site: MonitoringSite;
  } | null>(null);
  const [siteDeleteError, setSiteDeleteError] = useState<string | null>(null);
  const [isSiteDeleting, setIsSiteDeleting] = useState(false);
  const [snapshotModalSite, setSnapshotModalSite] = useState<MonitoringSite | null>(null);
  const [snapshotState, setSnapshotState] = useState<Record<number, SiteSnapshotState>>({});
  const [selectedDetectionForModal, setSelectedDetectionForModal] = useState<MonitoringSite | null>(null);
  const [isDetectionModalOpen, setIsDetectionModalOpen] = useState(false);

  const resetForm = useCallback(() => {
    setFormState(() => ({ ...CREATE_COMPETITOR_FORM_INITIAL }));
    setFormError(null);
    setIsSubmitting(false);
    setEditingCompetitor(null);
  }, []);

  useEffect(() => {
    console.log('[CompetitorTab] props updated', {
      productId,
      productName,
    });
  }, [productId, productName]);

  useEffect(() => {
    let isActive = true;

    const load = async () => {
      if (!productId) {
        if (!isActive) return;
        setCompetitors([]);
        setError(null);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        console.log('[CompetitorTab] Loading competitors', { productId });
        const competitorResponse = await monitoringService.getCompetitors(productId);
        const enriched = await Promise.all(
          competitorResponse.map(async (competitor) => {
            try {
              console.log('[CompetitorTab] Loading sites', { competitorId: competitor.id });
              const sites = await monitoringService.getSites(competitor.id);
              return { ...competitor, sites };
            } catch (siteError) {
              console.error(siteError);
              return { ...competitor, sites: [] };
            }
          })
        );

        if (isActive) {
          const sortedCompetitors = enriched.sort((a, b) => a.name.localeCompare(b.name, 'ja'));
          console.log('[CompetitorTab] Competitors loaded', {
            productId,
            competitorCount: sortedCompetitors.length,
          });
          setCompetitors(sortedCompetitors);

          // ⑦: 有効な監視サイトを持つ場合のみ監視ONで開始（新規商品は監視サイトが無いためOFF）。
          const hasActiveSites = sortedCompetitors.some((competitor) =>
            typeof competitor.active_sites_count === 'number'
              ? competitor.active_sites_count > 0
              : (competitor.sites || []).some(
                  (site: any) => site.status_label === 'active' || site.status === 1
                )
          );
          setMonitoringEnabled(hasActiveSites);
        }
      } catch (err) {
        console.error('[CompetitorTab] Failed to load competitors', err);

        if (isActive) {
          setError('競合データの取得に失敗しました');
          setCompetitors([]);
        }
      } finally {
        if (isActive) {
          setIsLoading(false);
        }
      }
    };

    load();

    return () => {
      isActive = false;
    };
  }, [productId, reloadKey]);

  const loadSiteChangeEvents = useCallback(
    async (siteId: number, options?: { page?: number; append?: boolean }) => {
      setSiteChangeEvents((prev) => {
        const current = prev[siteId] ?? createInitialChangeEventState();

        return {
          ...prev,
          [siteId]: {
            ...current,
            error: null,
            isLoading: options?.append ? current.isLoading : true,
            isLoadingMore: Boolean(options?.append),
          },
        };
      });

      try {
        const response = await monitoringService.getChangeDetections(siteId, {
          page: options?.page,
        });

        setSiteChangeEvents((prev) => {
          const current = prev[siteId] ?? createInitialChangeEventState();
          const mergedEvents = options?.append
            ? [...current.events, ...response.data]
            : response.data;

          return {
            ...prev,
            [siteId]: {
              ...current,
              events: mergedEvents,
              meta: response.meta,
              links: response.links,
              isLoading: false,
              isLoadingMore: false,
              error: null,
              initialized: true,
            },
          };
        });
      } catch (err) {
        console.error(err);
        setSiteChangeEvents((prev) => {
          const current = prev[siteId] ?? createInitialChangeEventState();

          return {
            ...prev,
            [siteId]: {
              ...current,
              isLoading: false,
              isLoadingMore: false,
              error: '変更履歴の取得に失敗しました。',
              initialized: true,
            },
          };
        });
      }
    },
    []
  );

  const loadSnapshots = useCallback(
    async (siteId: number, options?: { page?: number; append?: boolean }) => {
      setSnapshotState((prev) => {
        const current = prev[siteId] ?? createInitialSnapshotState();

        return {
          ...prev,
          [siteId]: {
            ...current,
            error: null,
            isLoading: options?.append ? current.isLoading : true,
            isLoadingMore: Boolean(options?.append),
          },
        };
      });

      try {
        const response = await monitoringService.getSnapshots(siteId, {
          page: options?.page,
        });

        setSnapshotState((prev) => {
          const current = prev[siteId] ?? createInitialSnapshotState();
          const mergedSnapshots = options?.append
            ? [...current.snapshots, ...response.data]
            : response.data;

          return {
            ...prev,
            [siteId]: {
              snapshots: mergedSnapshots,
              meta: response.meta,
              isLoading: false,
              isLoadingMore: false,
              error: null,
            },
          };
        });
      } catch (err) {
        console.error(err);
        setSnapshotState((prev) => {
          const current = prev[siteId] ?? createInitialSnapshotState();

          return {
            ...prev,
            [siteId]: {
              ...current,
              isLoading: false,
              isLoadingMore: false,
              error: 'スクリーンショットの取得に失敗しました。',
            },
          };
        });
      }
    },
    []
  );

  useEffect(() => {
    if (competitorSubTab !== '変更履歴') {
      return;
    }

    const sites = competitors.flatMap((competitor) => competitor.sites ?? []);

    sites.forEach((site) => {
      const state = siteChangeEvents[site.id];
      if (!state || (!state.initialized && !state.isLoading && !state.isLoadingMore)) {
        loadSiteChangeEvents(site.id);
      }
    });
  }, [competitorSubTab, competitors, loadSiteChangeEvents, siteChangeEvents]);

  useEffect(() => {
    resetForm();
    autoPromptedProductIdRef.current = null;
    setIsSuggestionModalOpen(false);
    setSuggestions([]);
    setSuggestionsGeneratedAt(null);
    setSuggestionError(null);
    setSuggestionSaveError(null);
    setIsModalOpen(false);
    setDeleteTarget(null);
    setDeleteError(null);
    setIsDeleting(false);
    setIsSiteModalOpen(false);
    setSiteModalCompetitor(null);
    setSiteForm(createInitialSiteForm());
    setSiteFormError(null);
    setIsSiteSubmitting(false);
    setEditingSite(null);
    setSiteDeleteTarget(null);
    setSiteDeleteError(null);
    setIsSiteDeleting(false);
    setSnapshotModalSite(null);
  }, [productId, resetForm]);

  useEffect(() => {
    if (
      productId &&
      !isLoading &&
      !error &&
      competitors.length === 0 &&
      autoPromptedProductIdRef.current !== productId
    ) {
      autoPromptedProductIdRef.current = productId;
      setIsSuggestionModalOpen(true);
      void loadSuggestions(false);
    }
  }, [competitors.length, error, isLoading, loadSuggestions, productId]);

  useEffect(() => {
    setSiteChangeEvents({});
    setSnapshotState({});
  }, [productId, reloadKey]);

  const productLabel = productName ?? (productId ? `ID: ${productId}` : undefined);

  const handleSnapshotLoadMore = (siteId: number) => {
    const current = snapshotState[siteId];
    if (!current?.meta) {
      return;
    }

    const nextPage = current.meta.current_page + 1;
    if (nextPage > current.meta.last_page || current.isLoadingMore) {
      return;
    }

    loadSnapshots(siteId, { page: nextPage, append: true });
  };


  const handleFormInputChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = event.target;
    const field = name as keyof CreateCompetitorFormState;

    setFormState((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleCreateModalOpen = () => {
    if (!productId) {
      return;
    }
    resetForm();
    setIsModalOpen(true);
  };

  const handleSnapshotModalOpen = (site: MonitoringSite) => {
    setSnapshotModalSite(site);
    loadSnapshots(site.id);
  };

  const handleSnapshotModalClose = () => {
    setSnapshotModalSite(null);
  };

  const handleEditModalOpen = (competitor: CompetitorWithSites) => {
    if (!productId) {
      return;
    }

    setEditingCompetitor(competitor);
    setFormState({
      name: competitor.name,
      websiteUrl: getCompetitorWebsiteUrl(competitor),
      description: competitor.description ?? '',
      tagsInput: competitor.tags?.join(', ') ?? '',
    });
    setFormError(null);
    setIsSubmitting(false);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    resetForm();
  };

  const handleSubmitCompetitor = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!productId && !editingCompetitor) {
      setFormError('対象プロダクトが選択されていません。');
      return;
    }

    const trimmedName = formState.name.trim();
    if (!trimmedName) {
      setFormError('競合名は必須です。');
      return;
    }

    const websiteUrl = formState.websiteUrl.trim();
    const description = formState.description.trim();
    const tags = formState.tagsInput
      .split(',')
      .map((tag) => tag.trim())
      .filter((tag) => tag.length > 0);

    setFormError(null);
    setIsSubmitting(true);

    try {
      const payload: CreateCompetitorPayload = {
        name: trimmedName,
        website_url: websiteUrl ? websiteUrl : undefined,
        description: description ? description : undefined,
        tags: tags.length ? tags : undefined,
      };

      if (editingCompetitor) {
        await monitoringService.updateCompetitor(editingCompetitor.id, payload);
      } else if (productId) {
        await monitoringService.createCompetitor(productId, payload);
      }

      setIsModalOpen(false);
      resetForm();
      triggerReload();
    } catch (err: any) {
      console.error(err);
      const responseData = err?.response?.data;

      let message = editingCompetitor ? '競合の更新に失敗しました。' : '競合の追加に失敗しました。';
      if (responseData) {
        if (responseData.errors) {
          const messages = Object.values(responseData.errors).reduce<string[]>((acc, current) => {
            if (Array.isArray(current)) {
              return acc.concat(current);
            }
            if (typeof current === 'string') {
              acc.push(current);
            }
            return acc;
          }, []);

          if (messages.length > 0) {
            message = messages.join(' / ');
          }
        } else if (typeof responseData.message === 'string') {
          message = responseData.message;
        }
      }

      setFormError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteRequest = (competitor: CompetitorWithSites) => {
    setDeleteError(null);
    setIsDeleting(false);
    setDeleteTarget(competitor);
  };

  const handleDeleteModalClose = () => {
    if (isDeleting) {
      return;
    }

    setDeleteTarget(null);
    setDeleteError(null);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) {
      return;
    }

    setDeleteError(null);
    setIsDeleting(true);

    try {
      await monitoringService.deleteCompetitor(deleteTarget.id);
      setDeleteTarget(null);
      triggerReload();
    } catch (err: any) {
      console.error(err);
      const responseData = err?.response?.data;

      let message = '競合の削除に失敗しました。';
      if (responseData) {
        if (responseData.errors) {
          const messages = Object.values(responseData.errors).reduce<string[]>((acc, current) => {
            if (Array.isArray(current)) {
              return acc.concat(current);
            }
            if (typeof current === 'string') {
              acc.push(current);
            }
            return acc;
          }, []);

          if (messages.length > 0) {
            message = messages.join(' / ');
          }
        } else if (typeof responseData.message === 'string') {
          message = responseData.message;
        }
      }

      setDeleteError(message);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleSiteCreateModalOpen = (competitor: CompetitorWithSites) => {
    setSiteModalCompetitor(competitor);
    setEditingSite(null);
    setSiteForm(createInitialSiteForm());
    setSiteFormError(null);
    setIsSiteSubmitting(false);
    setIsSiteModalOpen(true);
  };

  const handleSiteEditModalOpen = (
    competitor: CompetitorWithSites,
    site: MonitoringSite
  ) => {
    setSiteModalCompetitor(competitor);
    setEditingSite(site);
    setSiteForm({
      title: site.title,
      url: site.url,
      pageType: site.page_type,
      status: site.status,
      // 分・時間単位の旧頻度(4以上)は選択肢から廃止済みのため「1日ごと」に読み替える
      frequencyType: site.frequency_type > 3 ? 0 : site.frequency_type,
      customIntervalDays: site.custom_interval_days ? String(site.custom_interval_days) : '',
      timezone: site.timezone ?? getPreferredTimezone(),
    });
    setSiteFormError(null);
    setIsSiteSubmitting(false);
    setIsSiteModalOpen(true);
  };

  const handleSiteModalClose = () => {
    if (isSiteSubmitting) {
      return;
    }

    setIsSiteModalOpen(false);
    setSiteModalCompetitor(null);
    setEditingSite(null);
    setSiteForm(createInitialSiteForm());
    setSiteFormError(null);
  };

  const handleSiteInputChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = event.target;

    setSiteForm((prev) => {
      if (name === 'pageType' || name === 'status' || name === 'frequencyType') {
        const numericValue = Number(value);
        const nextState = {
          ...prev,
          [name]: numericValue,
        } as SiteFormState;

        if (name === 'frequencyType' && numericValue !== 3) {
          nextState.customIntervalDays = prev.customIntervalDays;
        }

        return nextState;
      }

      return {
        ...prev,
        [name]: value,
      };
    });
  };

  const handleSiteSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!siteModalCompetitor) {
      setSiteFormError('対象の競合情報が見つかりません。');
      return;
    }

    const trimmedTitle = siteForm.title.trim();
    const trimmedUrl = siteForm.url.trim();
    const timezone = siteForm.timezone.trim() || TIMEZONE_FALLBACK;

    if (!trimmedTitle) {
      setSiteFormError('監視URLの名称は必須項目です。');
      return;
    }

    if (!trimmedUrl) {
      setSiteFormError('監視URLを入力してください。');
      return;
    }

    setSiteFormError(null);
    setIsSiteSubmitting(true);

    try {
      const customInterval = siteForm.frequencyType === 3
        ? Number(siteForm.customIntervalDays)
        : undefined;

      if (siteForm.frequencyType === 3) {
        if (!siteForm.customIntervalDays.trim()) {
          setSiteFormError('カスタム頻度を選択した場合は日数を入力してください。');
          setIsSiteSubmitting(false);
          return;
        }

        if (!customInterval || Number.isNaN(customInterval)) {
          setSiteFormError('カスタム頻度の日数は1以上の数値で入力してください。');
          setIsSiteSubmitting(false);
          return;
        }
      }

      if (siteForm.frequencyType === 3 && (!customInterval || Number.isNaN(customInterval))) {
        setSiteFormError('カスタム頻度の日数は1以上の数値で入力してください。');
        setIsSiteSubmitting(false);
        return;
      }

      const metadata: Record<string, unknown> = { ...(editingSite?.metadata ?? {}) };

      const payload: CreateMonitoredSitePayload = {
        title: trimmedTitle,
        url: trimmedUrl,
        page_type: siteForm.pageType,
        status: siteForm.status,
        frequency_type: siteForm.frequencyType,
        timezone,
        metadata,
      };

      if (siteForm.frequencyType === 3 && customInterval) {
        payload.custom_interval_days = customInterval;
      }

      if ('interval_minutes' in metadata) {
        delete metadata.interval_minutes;
      }

      if (editingSite) {
        await monitoringService.updateSite(editingSite.id, payload);
      } else {
        await monitoringService.createSite(siteModalCompetitor.id, payload);
      }

      setIsSiteModalOpen(false);
      setSiteModalCompetitor(null);
      setEditingSite(null);
      setSiteForm(createInitialSiteForm());
      triggerReload();
    } catch (err: any) {
      console.error(err);
      const responseData = err?.response?.data;

      let message = editingSite ? '監視URLの更新に失敗しました。' : '監視URLの追加に失敗しました。';
      if (responseData) {
        if (responseData.errors) {
          const messages = Object.values(responseData.errors).reduce<string[]>((acc, current) => {
            if (Array.isArray(current)) {
              return acc.concat(current);
            }
            if (typeof current === 'string') {
              acc.push(current);
            }
            return acc;
          }, []);

          if (messages.length > 0) {
            message = messages.join(' / ');
          }
        } else if (typeof responseData.message === 'string') {
          message = responseData.message;
        }
      }

      setSiteFormError(message);
    } finally {
      setIsSiteSubmitting(false);
    }
  };

  const handleSiteDeleteRequest = (
    competitor: CompetitorWithSites,
    site: MonitoringSite
  ) => {
    setSiteDeleteError(null);
    setIsSiteDeleting(false);
    setSiteDeleteTarget({ competitor, site });
  };

  const handleSiteDeleteModalClose = () => {
    if (isSiteDeleting) {
      return;
    }

    setSiteDeleteTarget(null);
    setSiteDeleteError(null);
  };

  const handleSiteDeleteConfirm = async () => {
    if (!siteDeleteTarget) {
      return;
    }

    setSiteDeleteError(null);
    setIsSiteDeleting(true);

    try {
      await monitoringService.deleteSite(siteDeleteTarget.site.id);
      setSiteDeleteTarget(null);
      triggerReload();
    } catch (err: any) {
      console.error(err);
      const responseData = err?.response?.data;

      let message = '監視URLの削除に失敗しました。';
      if (responseData) {
        if (responseData.errors) {
          const messages = Object.values(responseData.errors).reduce<string[]>((acc, current) => {
            if (Array.isArray(current)) {
              return acc.concat(current);
            }
            if (typeof current === 'string') {
              acc.push(current);
            }
            return acc;
          }, []);

          if (messages.length > 0) {
            message = messages.join(' / ');
          }
        } else if (typeof responseData.message === 'string') {
          message = responseData.message;
        }
      }

      setSiteDeleteError(message);
    } finally {
      setIsSiteDeleting(false);
    }
  };

  const parsedTags = useMemo(
    () =>
      formState.tagsInput
        .split(',')
        .map((tag) => tag.trim())
        .filter((tag) => tag.length > 0),
    [formState.tagsInput]
  );

  const descriptionLength = formState.description.length;
  const isEditMode = Boolean(editingCompetitor);
  const modalTitle = isEditMode ? '競合情報を編集' : '競合情報を追加';
  const primaryButtonLabel = isSubmitting
    ? isEditMode
      ? '更新中...'
      : '追加中...'
    : isEditMode
      ? '更新する'
      : '追加する';
  const modalDescription = isEditMode
    ? '内容を更新すると、関連する監視設定にも最新情報が反映されます。'
    : productId
      ? 'モニタリングしたい競合サイトを登録し、監視対象の整理に役立てましょう。'
      : '先に対象プロダクトを選択すると、競合を紐付けて登録できます。';

  const pageTypeLabelMap = useMemo(() => {
    const map = new Map<number, string>();
    PAGE_TYPE_OPTIONS.forEach((option) => map.set(option.value, option.label));
    return map;
  }, []);

  const statusLabelMap = useMemo(() => {
    const map = new Map<number, string>();
    STATUS_OPTIONS.forEach((option) => map.set(option.value, option.label));
    return map;
  }, []);

  const frequencyLabelMap = useMemo(() => {
    const map = new Map<number, string>();
    FREQUENCY_OPTIONS.forEach((option) => map.set(option.value, option.label));
    return map;
  }, []);

  const resolvePageTypeLabel = useCallback(
    (site: MonitoringSite) =>
      pageTypeLabelMap.get(site.page_type) ?? site.page_type_label ?? '不明',
    [pageTypeLabelMap]
  );

  const resolveStatusLabel = useCallback((label?: string | null) => {
    if (!label) return '不明';
    return STATUS_DISPLAY_LABEL[label] ?? label;
  }, []);

  const resolveRunStatusLabel = useCallback((label?: string | null) => {
    if (!label) return '未実行';
    return RUN_STATUS_DISPLAY_LABEL[label] ?? label;
  }, []);

  const resolveFrequencyLabel = useCallback(
    (site: MonitoringSite) => {
      // 分・時間単位の旧頻度(4以上)はサーバー側で1日間隔に丸められる
      if (site.frequency_type >= 4) {
        return '1日ごと';
      }

      if (site.frequency_label === 'custom') {
        return site.custom_interval_days
          ? `${site.custom_interval_days}日ごと`
          : 'カスタム';
      }

      if (site.frequency_type !== undefined) {
        const mapped = frequencyLabelMap.get(site.frequency_type);
        if (mapped) {
          return mapped;
        }
      }

      switch (site.frequency_label) {
        case 'daily':
          return '1日ごと';
        case 'every_3_days':
          return '3日ごと';
        case 'every_7_days':
          return '7日ごと';
        default:
          return site.frequency_label ?? '不明';
      }
    },
    [frequencyLabelMap]
  );

  const resolveChangeTypeDisplay = useCallback((label?: string | null) => {
    if (!label) return '分類なし';
    return CHANGE_TYPE_DISPLAY_LABEL[label] ?? label;
  }, []);

  const resolveSeverityDisplay = useCallback((label?: string | null) => {
    if (!label) return '未設定';
    return SEVERITY_DISPLAY_LABEL[label] ?? label;
  }, []);

  const resolveSeverityTone = useCallback((label?: string | null) => {
    if (!label) return 'severity--unknown';
    return SEVERITY_TONE_CLASS[label] ?? 'severity--unknown';
  }, []);

  const totalSites = useMemo(
    () =>
      competitors.reduce((acc, competitor) => acc + (competitor.sites?.length ?? 0), 0),
    [competitors]
  );
  const totalActiveSites = useMemo(
    () =>
      monitoringEnabled
        ? competitors.reduce(
            (acc, competitor) =>
              acc +
              (typeof competitor.active_sites_count === 'number'
                ? competitor.active_sites_count
                : competitor.sites.filter((site) => site.status_label === 'active' || site.status === 1).length),
            0,
          )
        : 0,
    [competitors, monitoringEnabled],
  );

  const isSiteEditMode = Boolean(editingSite);
  const siteModalTitle = isSiteEditMode ? '監視URLを編集' : '監視URLを追加';
  const sitePrimaryButtonLabel = isSiteSubmitting
    ? isSiteEditMode
      ? '更新中...'
      : '追加中...'
    : isSiteEditMode
      ? '更新する'
      : '追加する';
  const siteModalDescription = siteModalCompetitor
    ? `${siteModalCompetitor.name} の監視対象URLを${isSiteEditMode ? '更新' : '登録'}します。`
    : 'サイトURLを登録すると、定期的なスクリーンショット取得と差分検知が可能になります。';

  const formatDateTime = (value?: string | null) => {
    if (!value) return '未実行';
    return new Date(value).toLocaleString('ja-JP');
  };

  const renderMonitoringTab = () => {
    if (!productId) {
      return <TaskyEmptyState title="表示する製品が選択されていません。" />;
    }

    if (isLoading) {
      return (
        <div className="flex items-center justify-center gap-3 py-12 text-sm text-gray-500">
          <TaskySpinner size="md" />
          競合情報を読み込み中です...
        </div>
      );
    }

    if (error) {
      return <TaskyErrorState description={error} />;
    }

    if (!competitors.length) {
      return (
        <TaskyEmptyState
          title="競合情報が登録されていません。"
        />
      );
    }

    return (
      <div className="flex flex-col gap-5">
        {productLabel && (
          <div className="text-sm text-gray-500">対象プロダクト: {productLabel}</div>
        )}
        {/* サマリーバー（モック .cm-summarybar 準拠） */}
        <div className="flex flex-wrap items-center gap-5 rounded-xl border border-warm-border bg-white px-4 py-2.5">
          <div className="text-xs text-gray-500">
            登録競合数: <b className="ml-0.5 text-[13px] font-extrabold text-dark">{competitors.length}</b>
          </div>
          <div className="text-xs text-gray-500">
            監視URL数: <b className="ml-0.5 text-[13px] font-extrabold text-dark">{totalSites}</b>
          </div>
          <div className="text-xs text-gray-500">
            アクティブ: <b className="ml-0.5 text-[13px] font-extrabold text-dark">{totalActiveSites}</b>
          </div>
          <label className="ml-auto inline-flex cursor-pointer select-none items-center gap-2">
            <input
              type="checkbox"
              className="sr-only"
              checked={monitoringEnabled}
              onChange={(event) => setMonitoringEnabled(event.target.checked)}
            />
            <span
              className={`relative h-[22px] w-10 rounded-full transition-colors ${
                monitoringEnabled ? 'bg-success' : 'bg-gray-300'
              }`}
            >
              <span
                className={`absolute top-0.5 h-[18px] w-[18px] rounded-full bg-white transition-all ${
                  monitoringEnabled ? 'left-5' : 'left-0.5'
                }`}
              />
            </span>
            <span
              className={`text-[11px] font-extrabold ${
                monitoringEnabled ? 'text-[#2E9B7E]' : 'text-gray-400'
              }`}
            >
              {monitoringEnabled ? '競合監視 ON' : '競合監視 OFF'}
            </span>
          </label>
        </div>
        {!monitoringEnabled && (
          <div className="flex items-center gap-2 rounded-[10px] border border-[#EAD9BF] bg-[#FBF5EC] px-3.5 py-2.5 text-xs text-[#A87C2B]">
            <span className="material-icons-round text-[16px]">pause_circle</span>
            競合監視は停止中です。すべてのURLは停止中として表示され、定期実行操作は非表示になります。
          </div>
        )}
        <div className="flex flex-col gap-4">
          {competitors.map((competitor) => (
            <div
              key={competitor.id}
              className="bg-white border border-warm-border rounded-[14px] px-[18px] py-4 flex flex-col gap-3"
              style={{ boxShadow: '0 0 18px 0 rgba(31,23,18,.06)' }}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex flex-col gap-1.5 min-w-0">
                  <div className="text-[14.5px] font-extrabold text-dark">{competitor.name}</div>
                  {competitor.tags?.length ? (
                    <div className="flex flex-wrap gap-1.5">
                      {competitor.tags.map((tag) => (
                        <span
                          key={tag}
                          className="rounded-full bg-[#F2F1ED] px-2 py-0.5 text-[10px] font-bold text-gray-500"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  ) : null}
                </div>
                <div className="flex flex-wrap items-center justify-end gap-2 shrink-0">
                  {getCompetitorWebsiteUrl(competitor) && (
                    <a
                      href={getCompetitorWebsiteUrl(competitor)}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 whitespace-nowrap text-[11.5px] font-semibold text-primary hover:underline"
                    >
                      <ExternalLink className="w-3.5 h-3.5" />
                      公式サイトを開く
                    </a>
                  )}
                  <button
                    type="button"
                    className="rounded-[7px] border border-warm-border bg-warm-surface px-2.5 py-1 text-[11px] font-semibold text-gray-600 transition-colors hover:bg-warm-hover"
                    onClick={() => handleEditModalOpen(competitor)}
                  >
                    編集
                  </button>
                  <button
                    type="button"
                    className="rounded-[7px] border border-warm-border bg-warm-surface px-2.5 py-1 text-[11px] font-semibold text-destructive transition-colors hover:bg-warm-hover"
                    onClick={() => handleDeleteRequest(competitor)}
                  >
                    削除
                  </button>
                </div>
              </div>

              {competitor.description && (
                <p className="text-xs leading-relaxed text-[#5A5D6E]">{competitor.description}</p>
              )}

              <div className="flex flex-wrap items-center justify-between gap-3 border-t border-dashed border-warm-border pt-3">
                <div className="flex flex-wrap gap-x-6 gap-y-2">
                  <div className="flex flex-col gap-px">
                    <span className="text-[10px] font-bold text-gray-400">監視URL</span>
                    <span className="text-[13px] font-extrabold text-dark">{competitor.sites.length}</span>
                  </div>
                  <div className="flex flex-col gap-px">
                    <span className="text-[10px] font-bold text-gray-400">アクティブ</span>
                    <span className="text-[13px] font-extrabold text-dark">
                      {monitoringEnabled
                        ? typeof competitor.active_sites_count === 'number'
                          ? competitor.active_sites_count
                          : '-'
                        : 0}
                    </span>
                  </div>
                  <div className="flex flex-col gap-px">
                    <span className="text-[10px] font-bold text-gray-400">最終同期</span>
                    <span className="text-[13px] font-extrabold text-dark">{formatDateTime(competitor.last_synced_at)}</span>
                  </div>
                </div>
                <button
                  type="button"
                  className="inline-flex items-center gap-1 rounded-lg border border-dashed border-[#C7CCEF] px-2.5 py-1.5 text-[11.5px] font-bold text-primary-hover transition-colors hover:bg-primary/5"
                  onClick={() => handleSiteCreateModalOpen(competitor)}
                >
                  <Plus className="w-3.5 h-3.5" />
                  監視URLを追加
                </button>
              </div>

              {competitor.sites.length ? (
                competitor.sites.map((site) => (
                  <div
                    key={site.id}
                    className="flex items-start justify-between gap-3 rounded-[10px] border border-[#EDEBE5] bg-warm-card p-3"
                  >
                    <div className="flex items-start gap-2.5 min-w-0">
                      <div className="flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-lg bg-primary text-[13px] font-extrabold uppercase text-white">
                        {site.title.slice(0, 1)}
                      </div>
                      <div className="flex flex-col gap-1 min-w-0">
                        <div className="text-[12.5px] font-bold text-dark">{site.title}</div>
                        <div className="flex flex-wrap items-center gap-1.5 text-[10.5px] text-gray-500">
                          <a
                            className="text-primary hover:underline break-all"
                            href={site.url}
                            target="_blank"
                            rel="noreferrer"
                          >
                            {site.url}
                          </a>
                          <span className="rounded-[5px] bg-[#EDEBE5] px-1.5 py-px text-[9.5px] font-bold text-gray-500">
                            {resolvePageTypeLabel(site)}
                          </span>
                          <span className="rounded-[5px] bg-[#EDEBE5] px-1.5 py-px text-[9.5px] font-bold text-gray-500">
                            {resolveFrequencyLabel(site)}
                          </span>
                        </div>
                        <div className="flex flex-wrap gap-2 text-[10.5px] text-gray-400">
                          <span>最終チェック: {formatDateTime(site.last_run_at)}</span>
                          <span>次回予定: {formatDateTime(site.next_run_at)}</span>
                        </div>
                        <div className="flex flex-wrap gap-2 text-[10.5px] text-gray-400">
                          <span>タイムゾーン: {site.timezone}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-2 shrink-0">
                      <div className="flex flex-wrap items-center justify-end gap-1.5">
                        <span
                          className="rounded-full px-2 py-0.5 text-[10px] font-extrabold"
                          style={
                            monitoringEnabled
                              ? SITE_STATUS_PILL_STYLE[site.status_label] ?? SITE_STATUS_PILL_FALLBACK
                              : SITE_STATUS_PILL_FALLBACK
                          }
                        >
                          {monitoringEnabled ? resolveStatusLabel(site.status_label) : '停止中'}
                        </span>
                        {monitoringEnabled && (
                          <span
                            className="text-[10.5px] font-bold"
                            style={{ color: RUN_STATUS_TEXT_COLOR[site.last_run_status_label] ?? '#8A90A4' }}
                          >
                            {resolveRunStatusLabel(site.last_run_status_label)}
                          </span>
                        )}
                      </div>
                      <div className="flex gap-1.5">
                        <button
                          type="button"
                          className="rounded-[7px] border border-warm-border bg-warm-surface px-2 py-1 text-[11px] font-semibold text-gray-600 transition-colors hover:bg-warm-hover"
                          onClick={() => handleSiteEditModalOpen(competitor, site)}
                        >
                          編集
                        </button>
                        <button
                          type="button"
                          className="rounded-[7px] border border-warm-border bg-warm-surface px-2 py-1 text-[11px] font-semibold text-gray-600 transition-colors hover:bg-warm-hover"
                          onClick={() => handleSnapshotModalOpen(site)}
                        >
                          一覧
                        </button>
                        <button
                          type="button"
                          className="rounded-[7px] border border-warm-border bg-warm-surface px-2 py-1 text-[11px] font-semibold text-destructive transition-colors hover:bg-warm-hover"
                          onClick={() => handleSiteDeleteRequest(competitor, site)}
                        >
                          削除
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="rounded-[10px] border border-dashed border-warm-border bg-[#FAFAF8] p-4 text-center text-xs text-gray-400">
                  監視URLが登録されていません。
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderSnapshotModal = () => {
    if (!snapshotModalSite) {
      return null;
    }

    const state = snapshotState[snapshotModalSite.id] ?? createInitialSnapshotState();
    const hasMore = state.meta ? state.meta.current_page < state.meta.last_page : false;

    return (
      <SimpleModal
        isOpen={Boolean(snapshotModalSite)}
        onClose={handleSnapshotModalClose}
        title={`${snapshotModalSite.title} のスクリーンショット一覧`}
      >
        <div className="flex flex-col gap-4">
          {state.error && <TaskyErrorState description={state.error} />}

          {state.isLoading && !state.isLoadingMore ? (
            <div className="flex items-center justify-center gap-3 py-10 text-sm text-gray-500">
              <TaskySpinner size="md" />
              スクリーンショットを読み込み中です...
            </div>
          ) : state.snapshots.length === 0 ? (
            <TaskyEmptyState title="まだスクリーンショットが記録されていません。" />
          ) : (
            <div className="grid grid-cols-[repeat(auto-fill,minmax(220px,1fr))] gap-4">
              {state.snapshots.map((snapshot) => (
                <div
                  key={snapshot.id}
                  className="flex flex-col overflow-hidden bg-white border border-warm-border rounded-tasky-md"
                >
                  <div className="bg-warm-surface border-b border-warm-border">
                    <img
                      src={snapshot.image_url}
                      alt={`${snapshot.id}`}
                      className="block w-full h-[140px] object-cover"
                    />
                  </div>
                  <div className="flex flex-col gap-2 p-3">
                    <div className="text-xs text-gray-500">
                      撮影日時: {snapshot.captured_at ? new Date(snapshot.captured_at).toLocaleString('ja-JP') : '不明'}
                    </div>
                    <div>
                      <a
                        href={snapshot.image_url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-xs text-primary hover:underline"
                      >
                        新しいタブで表示
                      </a>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {hasMore && (
            <div className="flex justify-center">
              <TaskyButton
                type="button"
                variant="outline"
                size="sm"
                onClick={() => handleSnapshotLoadMore(snapshotModalSite.id)}
                disabled={state.isLoadingMore}
              >
                {state.isLoadingMore ? '読み込み中...' : 'さらに読み込む'}
              </TaskyButton>
            </div>
          )}
        </div>
      </SimpleModal>
    );
  };

  const renderSiteChangeHistory = (site: MonitoringSite) => {
    const state = siteChangeEvents[site.id] ?? createInitialChangeEventState();
    const events = state.events;
    const isInitialLoading = state.isLoading && !state.isLoadingMore;
    const hasMore = state.meta ? state.meta.current_page < state.meta.last_page : false;
    const nextPage = state.meta ? state.meta.current_page + 1 : 2;

    return (
      <div key={site.id} className="change-history__site">
        <div className="change-history__site-header">
          <div className="change-history__site-info">
            <div className="change-history__site-avatar" aria-hidden>
              {site.title.charAt(0).toUpperCase()}
            </div>
            <div>
              <div className="change-history__site-title">{site.title}</div>
              <a
                href={site.url}
                target="_blank"
                rel="noreferrer"
                className="change-history__site-link"
              >
                {site.url}
              </a>
            </div>
          </div>
          <div className="change-history__site-actions">
            <button
              type="button"
              className="btn-link"
              onClick={() => loadSiteChangeEvents(site.id)}
              disabled={state.isLoading || state.isLoadingMore}
            >
              再読み込み
            </button>
          </div>
        </div>

        {state.error && <div className="error-message mb-4 flex items-center justify-between border border-red-200">{state.error}</div>}

        {isInitialLoading && (
          <div className="loading-message change-history__loading">
            変更履歴を取得しています...
          </div>
        )}

        {!isInitialLoading && !state.error && events.length === 0 && (
          <div className="empty-state flex flex-1 flex-col items-center justify-center gap-3 px-6 py-16 text-[14px] text-gray-400">
            まだ検出された変更履歴はありません。
          </div>
        )}

        {events.length > 0 && (
          <ul className="change-history__events">
            {events.map((event) => (
              <li key={event.id} className="change-history__event">
                <span className="change-history__event-node" aria-hidden />
                <div className="change-history__event-body">
                  <div className="change-history__event-meta">
                    <span
                      className={`change-history__severity ${resolveSeverityTone(event.severity_level)}`}
                    >
                      {resolveSeverityDisplay(event.severity_level)}
                    </span>
                    <span className="change-history__timestamp">
                      {formatDateTime(event.detected_at)}
                    </span>
                  </div>

                  {event.description && (
                    <div className="change-history__summary">{event.description}</div>
                  )}

                  {event.change_summary && (
                    <div className="change-history__change-summary">
                      {event.change_summary}
                    </div>
                  )}

                  <div className="change-history__stats">
                    {event.has_visual_changes && (
                      <div className="change-history__stat">
                        <span>視覚的変更:</span> {event.visual_diff_percentage.toFixed(2)}%
                      </div>
                    )}
                    {event.has_content_changes && (
                      <div className="change-history__stat">
                        <span>コンテンツ変更:</span> あり
                      </div>
                    )}
                  </div>

                  <div className="change-history__footer">
                    <div className="change-history__links">
                      {event.diff_image_url && (
                        <a
                          href={event.diff_image_url}
                          target="_blank"
                          rel="noreferrer"
                        >
                          差分画像
                        </a>
                      )}
                      {event.current_snapshot?.screenshot_url && (
                        <a
                          href={event.current_snapshot.screenshot_url}
                          target="_blank"
                          rel="noreferrer"
                        >
                          最新スクリーンショット
                        </a>
                      )}
                      {event.previous_snapshot?.screenshot_url && (
                        <a
                          href={event.previous_snapshot.screenshot_url}
                          target="_blank"
                          rel="noreferrer"
                        >
                          前回スクリーンショット
                        </a>
                      )}
                      <button
                        type="button"
                        className="btn-view-details"
                        onClick={() => {
                          setSelectedDetectionForModal(site);
                          setIsDetectionModalOpen(true);
                        }}
                      >
                        詳細表示
                      </button>
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}

        {hasMore && (
          <div className="change-history__load-more">
            <button
              type="button"
              className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-gray-300 bg-white px-5 py-2.5 text-sm font-medium text-gray-700 transition-all hover:enabled:border-gray-400 hover:enabled:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
              onClick={() => loadSiteChangeEvents(site.id, { page: nextPage, append: true })}
              disabled={state.isLoadingMore}
            >
              {state.isLoadingMore ? '読み込み中...' : 'さらに表示'}
            </button>
          </div>
        )}
      </div>
    );
  };

  const renderChangeHistoryTab = () => {
    if (!productId) {
      return <div className="empty-state flex flex-1 flex-col items-center justify-center gap-3 px-6 py-16 text-[14px] text-gray-400">表示する製品が選択されていません。</div>;
    }

    if (isLoading) {
      return <div className="loading-message">変更履歴を読み込み中です...</div>;
    }

    if (error) {
      return <div className="error-message mb-4 flex items-center justify-between border border-red-200">{error}</div>;
    }

    if (!competitors.length) {
      return (
        <div className="empty-state flex flex-1 flex-col items-center justify-center gap-3 px-6 py-16 text-[14px] text-gray-400">競合情報が登録されていません。</div>
      );
    }

    return (
      <div className="change-history">
        {competitors.map((competitor) => (
          <div key={competitor.id} className="change-history__competitor">
            <div className="change-history__competitor-header">
              <div>
                <div className="change-history__competitor-name">{competitor.name}</div>
                {getCompetitorWebsiteUrl(competitor) && (
                  <a
                    href={getCompetitorWebsiteUrl(competitor)}
                    target="_blank"
                    rel="noreferrer"
                    className="change-history__competitor-link"
                  >
                    {getCompetitorWebsiteUrl(competitor)}
                  </a>
                )}
              </div>
              {competitor.tags?.length ? (
                <div className="change-history__competitor-tags">
                  {competitor.tags.map((tag) => (
                    <span key={tag} className="change-history__tag">
                      {tag}
                    </span>
                  ))}
                </div>
              ) : null}
            </div>

            <div className="change-history__sites">
              {competitor.sites?.length ? (
                competitor.sites.map((site) => renderSiteChangeHistory(site))
              ) : (
                <div className="">監視URLが登録されていません。</div>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div>
      <div>
        {/* DCMモック準拠のパネル見出し */}
        <div className="mt-2 mb-4 flex items-end justify-between">
          <h2 className="flex items-center gap-2 text-lg font-bold text-dark">
            <span className="material-icons-round text-[21px] text-primary">travel_explore</span>
            競合情報
            <span className="help-tip">
              <span className="material-icons-round">help_outline</span>
              <span className="tip-body">
                <b>競合の動きを見張る場所。</b>競合サイトのURLを登録すると、
                定期的にスクリーンショットを取得して価格・CTA・コンテンツの変更を検知します。
                検知した変更は「変更履歴」に時系列で並びます。
              </span>
            </span>
          </h2>
        </div>

        {/* サブタブ（モック .cm-subtabs 準拠のピル型） */}
        <div className="mb-3.5 flex items-center gap-1 border-b border-warm-border pb-2.5">
          {(['監視設定', '変更履歴'] as const).map((t) => (
            <button
              key={t}
              className={`rounded-lg px-3 py-1.5 text-[12.5px] font-bold transition-colors ${
                competitorSubTab === t
                  ? 'bg-primary text-white'
                  : 'text-[#8A90A4] hover:bg-[#F2F1ED]'
              }`}
              onClick={() => setCompetitorSubTab(t)}
            >
              {t}
            </button>
          ))}
          <div className="flex-1" />
          <TaskyButton
            type="button"
            variant="outline"
            size="sm"
            onClick={competitors.length === 0 ? handleSuggestionModalOpen : handleCreateModalOpen}
            disabled={!productId}
            title={productId ? undefined : '先に対象プロダクトを選択してください'}
          >
            <Plus />
            {competitors.length === 0 ? '競合を調査' : '競合情報を追加'}
          </TaskyButton>
        </div>

        {competitorSubTab === '監視設定' && renderMonitoringTab()}

       {competitorSubTab === '変更履歴' && <ChangeHistoryTab productId={productId} productName={productName} />}
      </div>

      {renderSnapshotModal()}

      <SimpleModal
        isOpen={isSuggestionModalOpen}
        onClose={() => !isSavingSuggestions && setIsSuggestionModalOpen(false)}
        title="競合分析を調査中です"
        size="lg"
      >
        <div className="flex flex-col gap-5">
          <div className="rounded-tasky-md border border-warm-border bg-warm-surface px-5 py-4">
            <p className="text-sm leading-relaxed text-gray-600">
              登録したい競合を選択してください。
            </p>
            {productLabel && (
              <div className="mt-2 text-xs font-medium text-primary">
                対象プロダクト: {productLabel}
              </div>
            )}
          </div>

          {suggestionError && <TaskyErrorState description={suggestionError} />}
          {suggestionSaveError && <TaskyErrorState description={suggestionSaveError} />}

          {isSuggestionsLoading && suggestions.length === 0 ? (
            <div className="flex items-center justify-center gap-3 py-12 text-sm text-gray-500">
              <TaskySpinner size="md" />
              商品の内容から競合分析を調査中です...
            </div>
          ) : suggestions.length > 0 ? (
            <div className="flex flex-col gap-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <h3 className="text-sm font-semibold text-dark">調査した競合候補</h3>
                  {suggestionsGeneratedAt && (
                    <p className="mt-1 text-xs text-gray-400">
                      最終調査: {formatDateTime(suggestionsGeneratedAt)}
                    </p>
                  )}
                </div>
                {isRefreshingSuggestions && (
                  <div className="flex items-center gap-2 text-xs text-primary">
                    <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                    一覧を表示したまま再検索中...
                  </div>
                )}
              </div>

              {suggestions.map((suggestion, index) => (
                <label
                  key={`${suggestion.name}-${index}`}
                  className={`flex cursor-pointer items-start gap-3 rounded-tasky-md border p-4 transition-colors ${
                    suggestion.selected
                      ? 'border-primary bg-primary/5'
                      : 'border-warm-border bg-white hover:bg-warm-hover'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={suggestion.selected}
                    onChange={() => handleSuggestionToggle(index)}
                    disabled={isSavingSuggestions}
                    className="mt-1 h-4 w-4 accent-primary"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-semibold text-dark">{suggestion.name}</span>
                      {suggestion.company_url && (
                        <a
                          href={suggestion.company_url}
                          target="_blank"
                          rel="noreferrer"
                          onClick={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                            window.open(suggestion.company_url!, '_blank', 'noopener,noreferrer');
                          }}
                          className="inline-flex items-center gap-1 break-all text-xs text-primary hover:underline"
                        >
                          <ExternalLink className="h-3.5 w-3.5" />
                          {suggestion.company_url}
                        </a>
                      )}
                    </div>
                    {suggestion.description && (
                      <p className="mt-2 text-sm leading-relaxed text-gray-600">
                        {suggestion.description}
                      </p>
                    )}
                    {(suggestion.strengths || suggestion.weaknesses) && (
                      <div className="mt-2 grid gap-2 text-xs text-gray-500 sm:grid-cols-2">
                        {suggestion.strengths && <div>強み: {suggestion.strengths}</div>}
                        {suggestion.weaknesses && <div>弱み: {suggestion.weaknesses}</div>}
                      </div>
                    )}
                  </div>
                </label>
              ))}
            </div>
          ) : !suggestionError ? (
            <TaskyEmptyState
              title="競合候補が見つかりませんでした。"
              description="商品情報を確認して、再度検索してください。"
            />
          ) : null}

          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-warm-border pt-4">
            <TaskyButton
              type="button"
              variant="outline"
              size="sm"
              onClick={() => void loadSuggestions(true)}
              disabled={isSuggestionsLoading || isRefreshingSuggestions || isSavingSuggestions}
            >
              <RefreshCw className={isRefreshingSuggestions ? 'animate-spin' : undefined} />
              {isRefreshingSuggestions ? '再検索中...' : '再度検索する'}
            </TaskyButton>
            <div className="flex items-center gap-3">
              <TaskyButton
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setIsSuggestionModalOpen(false)}
                disabled={isSavingSuggestions}
              >
                キャンセル
              </TaskyButton>
              <TaskyButton
                type="button"
                size="sm"
                onClick={handleSaveSuggestions}
                disabled={
                  isSuggestionsLoading ||
                  isRefreshingSuggestions ||
                  isSavingSuggestions ||
                  suggestions.every((suggestion) => !suggestion.selected)
                }
              >
                {isSavingSuggestions
                  ? '保存中...'
                  : `選択した競合を保存（${suggestions.filter((suggestion) => suggestion.selected).length}件）`}
              </TaskyButton>
            </div>
          </div>
        </div>
      </SimpleModal>

      <SimpleModal isOpen={isModalOpen} onClose={handleModalClose} title={modalTitle}>
        <form onSubmit={handleSubmitCompetitor} className="flex flex-col gap-6">
          <div className="flex items-start justify-between gap-4 bg-warm-surface border border-warm-border rounded-tasky-md px-5 py-4">
            <div>
              <h3 className="text-base font-semibold text-dark mb-1">
                {isEditMode ? '競合の基本情報を編集' : '競合の基本情報を登録'}
              </h3>
              <p className="text-sm text-gray-500 leading-relaxed">{modalDescription}</p>
            </div>
            {productLabel && (
              <TaskyBadge variant="info" className="whitespace-nowrap">
                {productLabel}
              </TaskyBadge>
            )}
          </div>

          {!productId && !isEditMode && (
            <TaskyAlert tone="warning">競合を追加する前にプロダクトを選択してください。</TaskyAlert>
          )}

          {formError && <TaskyErrorState description={formError} />}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="flex flex-col gap-2">
              <TaskyLabel htmlFor="competitor-name" required className="mb-0">
                競合名
              </TaskyLabel>
              <TaskyInput
                id="competitor-name"
                name="name"
                type="text"
                value={formState.name}
                onChange={handleFormInputChange}
                placeholder="例: SoundBeats Air X"
                autoFocus
                required
                disabled={isSubmitting}
              />
              <p className="text-xs text-gray-500">社内で把握している正式名称を入力してください。</p>
            </div>

            <div className="flex flex-col gap-2">
              <TaskyLabel htmlFor="competitor-website" className="mb-0">公式サイト URL</TaskyLabel>
              <TaskyInput
                id="competitor-website"
                name="websiteUrl"
                type="url"
                value={formState.websiteUrl}
                onChange={handleFormInputChange}
                placeholder="https://example.com"
                disabled={isSubmitting}
              />
              <p className="text-xs text-gray-500">競合サイトのトップページや特集ページなど、追跡したい URL を入力します。</p>
            </div>

            <div className="flex flex-col gap-2 md:col-span-2">
              <TaskyLabel htmlFor="competitor-description" className="mb-0">概要 / メモ</TaskyLabel>
              <TaskyTextarea
                id="competitor-description"
                name="description"
                rows={4}
                value={formState.description}
                onChange={handleFormInputChange}
                placeholder="主要な特徴、ターゲット層、キャンペーンなど気づいた点を記録"
                disabled={isSubmitting}
              />
              <div className="flex flex-wrap items-center justify-end gap-3">
                <span className="text-xs text-gray-400">{descriptionLength}文字</span>
              </div>
            </div>

            <div className="flex flex-col gap-2 md:col-span-2">
              <TaskyLabel htmlFor="competitor-tags" className="mb-0">タグ（カンマ区切り）</TaskyLabel>
              <TaskyInput
                id="competitor-tags"
                name="tagsInput"
                type="text"
                value={formState.tagsInput}
                onChange={handleFormInputChange}
                placeholder="例: ワイヤレス, ノイズキャンセリング"
                disabled={isSubmitting}
              />
              <p className="text-xs text-gray-500">複数入力する場合は「,」で区切ります。</p>
              {parsedTags.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {parsedTags.map((tag) => (
                    <TaskyBadge key={tag} variant="info">
                      {tag}
                    </TaskyBadge>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="flex justify-end gap-3">
            <TaskyButton type="button" variant="outline" size="sm" onClick={handleModalClose} disabled={isSubmitting}>
              キャンセル
            </TaskyButton>
            <TaskyButton type="submit" size="sm" disabled={isSubmitting || (!productId && !isEditMode)}>
              {primaryButtonLabel}
            </TaskyButton>
          </div>
        </form>
      </SimpleModal>

      <SimpleModal
        isOpen={isSiteModalOpen}
        onClose={handleSiteModalClose}
        title={siteModalTitle}
      >
        <form onSubmit={handleSiteSubmit} className="flex flex-col gap-6">
          <div className="flex items-start justify-between gap-4 bg-warm-surface border border-warm-border rounded-tasky-md px-5 py-4">
            <div>
              <h3 className="text-base font-semibold text-dark mb-1">
                {isSiteEditMode ? '監視URLの詳細を更新' : '監視URLを追加'}
              </h3>
              <p className="text-sm text-gray-500 leading-relaxed">{siteModalDescription}</p>
            </div>
            {siteModalCompetitor && (
              <TaskyBadge variant="info" className="whitespace-nowrap">
                {siteModalCompetitor.name}
              </TaskyBadge>
            )}
          </div>

          {siteFormError && <TaskyErrorState description={siteFormError} />}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="flex flex-col gap-2">
              <TaskyLabel htmlFor="site-title" required className="mb-0">
                URL名
              </TaskyLabel>
              <TaskyInput
                id="site-title"
                name="title"
                type="text"
                value={siteForm.title}
                onChange={handleSiteInputChange}
                placeholder="例: 料金ページ"
                required
                disabled={isSiteSubmitting}
              />
            </div>

            <div className="flex flex-col gap-2">
              <TaskyLabel htmlFor="site-url" required className="mb-0">
                監視URL
              </TaskyLabel>
              <TaskyInput
                id="site-url"
                name="url"
                type="url"
                value={siteForm.url}
                onChange={handleSiteInputChange}
                placeholder="https://example.com/pricing"
                required
                disabled={isSiteSubmitting}
              />
            </div>

            <div className="flex flex-col gap-2">
              <TaskyLabel htmlFor="site-page-type" className="mb-0">ページ種別</TaskyLabel>
              <TaskySelect
                id="site-page-type"
                name="pageType"
                value={siteForm.pageType}
                onChange={handleSiteInputChange}
                disabled={isSiteSubmitting}
              >
                {PAGE_TYPE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </TaskySelect>
            </div>

            <div className="flex flex-col gap-2">
              <TaskyLabel htmlFor="site-status" className="mb-0">ステータス</TaskyLabel>
              <TaskySelect
                id="site-status"
                name="status"
                value={siteForm.status}
                onChange={handleSiteInputChange}
                disabled={isSiteSubmitting}
              >
                {STATUS_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </TaskySelect>
            </div>

            <div className="flex flex-col gap-2">
              <TaskyLabel htmlFor="site-frequency" className="mb-0">監視頻度</TaskyLabel>
              <TaskySelect
                id="site-frequency"
                name="frequencyType"
                value={siteForm.frequencyType}
                onChange={handleSiteInputChange}
                disabled={isSiteSubmitting}
              >
                {FREQUENCY_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </TaskySelect>
            </div>

            {siteForm.frequencyType === 3 && (
              <div className="flex flex-col gap-2">
                <TaskyLabel htmlFor="site-custom-interval" required className="mb-0">
                  カスタム間隔（日）
                </TaskyLabel>
                <TaskyInput
                  id="site-custom-interval"
                  name="customIntervalDays"
                  type="number"
                  min={1}
                  max={30}
                  value={siteForm.customIntervalDays}
                  onChange={handleSiteInputChange}
                  placeholder="例: 5"
                  disabled={isSiteSubmitting}
                />
                <p className="text-xs text-gray-500">1〜30の範囲で入力してください。</p>
              </div>
            )}

            <div className="flex flex-col gap-2">
              <TaskyLabel htmlFor="site-timezone" className="mb-0">タイムゾーン</TaskyLabel>
              <TaskyInput
                id="site-timezone"
                name="timezone"
                type="text"
                value={siteForm.timezone}
                onChange={handleSiteInputChange}
                placeholder="例: Asia/Tokyo"
                disabled={isSiteSubmitting}
              />
            </div>
          </div>

          <div className="flex justify-end gap-3">
            <TaskyButton type="button" variant="outline" size="sm" onClick={handleSiteModalClose} disabled={isSiteSubmitting}>
              キャンセル
            </TaskyButton>
            <TaskyButton type="submit" size="sm" disabled={isSiteSubmitting}>
              {sitePrimaryButtonLabel}
            </TaskyButton>
          </div>
        </form>
      </SimpleModal>

      <SimpleModal
        isOpen={Boolean(deleteTarget)}
        onClose={handleDeleteModalClose}
        title="競合情報を削除"
      >
        <div className="flex flex-col gap-5">
          {deleteTarget && (
            <>
              <p className="text-sm leading-relaxed text-gray-600">
                <strong className="font-bold text-dark">{deleteTarget.name}</strong>
                を競合一覧から削除します。関連する監視URLは維持されますが、再度割り当てるまで分析には表示されません。
              </p>

              <div className="grid gap-3 bg-red-50 border border-red-200 rounded-tasky-md px-4 py-4">
                <div className="flex items-center justify-between gap-3">
                  <span className="text-[13px] font-semibold text-red-700">監視URL</span>
                  <span className="text-sm font-bold text-red-900">{deleteTarget.sites.length}</span>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <span className="text-[13px] font-semibold text-red-700">アクティブURL</span>
                  <span className="text-sm font-bold text-red-900">
                    {typeof deleteTarget.active_sites_count === 'number'
                      ? deleteTarget.active_sites_count
                      : '-'}
                  </span>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <span className="text-[13px] font-semibold text-red-700">最終同期</span>
                  <span className="text-sm font-bold text-red-900">{formatDateTime(deleteTarget.last_synced_at)}</span>
                </div>
              </div>
            </>
          )}

          {deleteError && <TaskyErrorState description={deleteError} />}

          <div className="flex justify-end gap-3">
            <TaskyButton type="button" variant="outline" size="sm" onClick={handleDeleteModalClose} disabled={isDeleting}>
              キャンセル
            </TaskyButton>
            <TaskyButton type="button" variant="destructive" size="sm" onClick={handleDeleteConfirm} disabled={isDeleting}>
              {isDeleting ? '削除中...' : '削除する'}
            </TaskyButton>
          </div>
        </div>
      </SimpleModal>

      <SimpleModal
        isOpen={Boolean(siteDeleteTarget)}
        onClose={handleSiteDeleteModalClose}
        title="監視URLを削除"
      >
        <div className="flex flex-col gap-5">
          {siteDeleteTarget && (
            <>
              <p className="text-sm leading-relaxed text-gray-600">
                <strong className="font-bold text-dark">{siteDeleteTarget.site.title}</strong>
                を {siteDeleteTarget.competitor.name} の監視一覧から削除します。ステータスは停止に変更されます。
              </p>

              <div className="grid gap-3 bg-red-50 border border-red-200 rounded-tasky-md px-4 py-4">
                <div className="flex items-center justify-between gap-3">
                  <span className="text-[13px] font-semibold text-red-700">URL</span>
                  <span className="text-sm font-bold text-red-900 break-all text-right">{siteDeleteTarget.site.url}</span>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <span className="text-[13px] font-semibold text-red-700">ページ種別</span>
                  <span className="text-sm font-bold text-red-900">
                    {resolvePageTypeLabel(siteDeleteTarget.site)}
                  </span>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <span className="text-[13px] font-semibold text-red-700">監視頻度</span>
                  <span className="text-sm font-bold text-red-900">
                    {resolveFrequencyLabel(siteDeleteTarget.site)}
                  </span>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <span className="text-[13px] font-semibold text-red-700">最終チェック</span>
                  <span className="text-sm font-bold text-red-900">{formatDateTime(siteDeleteTarget.site.last_run_at)}</span>
                </div>
              </div>
            </>
          )}

          {siteDeleteError && <TaskyErrorState description={siteDeleteError} />}

          <div className="flex justify-end gap-3">
            <TaskyButton
              type="button"
              variant="outline"
              size="sm"
              onClick={handleSiteDeleteModalClose}
              disabled={isSiteDeleting}
            >
              キャンセル
            </TaskyButton>
            <TaskyButton
              type="button"
              variant="destructive"
              size="sm"
              onClick={handleSiteDeleteConfirm}
              disabled={isSiteDeleting}
            >
              {isSiteDeleting ? '削除中...' : '削除する'}
            </TaskyButton>
          </div>
        </div>
      </SimpleModal>

      {/* Change Detection Modal */}
      {selectedDetectionForModal && (
        <ChangeDetectionModal
          isOpen={isDetectionModalOpen}
          onClose={() => {
            setIsDetectionModalOpen(false);
            setSelectedDetectionForModal(null);
          }}
          site={selectedDetectionForModal}
        />
      )}
    </div>
  );
};

export default CompetitorTab;
